import { useState } from "react"
import Chip from "@/components/chip"
import getColor from "@/utils/get-color.utils"
import PhysicalRiskEditModal from "@/entities/physical-risk/ui/modal/PhysicalRiskEditModal"
import Modal from "@/components/modal"
import { WizardOverviewField } from "@/components/WizardOverview"
import { PhysicalRiskMeter } from "@/entities/physical-risk/ui/PhysicalRiskMeter"
import { getRiskFamilyImageUrl } from "@/entities/risk-family/model/risk-family.utils"
import { getPreventionActionStatusLabelFull } from "../../model/prevention-action.formatter"
import { useAppSelector } from "@/core/root-reducer"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import populationStore from "@/entities/population/model/population.store"
import { formatDocumentDate } from "@/entities/document/utils/document-file.utils"
import { isRpsPreventionAction, PreventionAction } from "../../model/prevention-action.types"
import {
  getRpsScopeLabel,
  isPreventionActionOverdue,
  isPreventionActionToPlan
} from "../../model/prevention-action.utils"

interface Props {
  action: PreventionAction
  onClose: () => void
}

export default function PreventionActionReadonlyModal({ action, onClose }: Props) {
  const [isRiskOpen, setIsRiskOpen] = useState(false)
  const responsible = useAppSelector((state) => state.user.list.find((user) => user.id === action.UserId))
  const risks = useAppSelector(physicalRiskStore.selectors.list)
  const risk = risks.find((item) => item.id === action.PhysicalRiskId)
  const populations = useAppSelector(populationStore.selectors.list)
  const riskImageUrl = risk?.cFamily ? getRiskFamilyImageUrl(risk.cFamily) : null
  const isOverdue = isPreventionActionOverdue(action)
  const isToPlan = isPreventionActionToPlan(action)
  const dateLabel = (date: Date | null) => (date ? formatDocumentDate(date) : undefined)
  const responsibleName = responsible?.username?.trim() || responsible?.email
  const statusLabel = getPreventionActionStatusLabelFull(action.cStatus, isOverdue, isToPlan)
  const fields = [
    {
      label: "Risques psychosociaux",
      value: isRpsPreventionAction(action) ? getRpsScopeLabel(action, populations) : undefined
    },
    { label: "Début prévu", value: dateLabel(action.dateStartedPlanned) },
    { label: "Échéance prévue", value: dateLabel(action.datePlanned) },
    { label: "Début réel", value: dateLabel(action.dateStarted) },
    { label: "Date de réalisation", value: dateLabel(action.dateDone) },
    { label: "Renouvellement", value: action.refreshDelay ? `Tous les ${action.refreshDelay} mois` : undefined },
    { label: "Responsable", value: responsibleName },
    {
      label: "Budget",
      value:
        action.cost == null ? undefined : action.cost.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })
    },
    { label: "Financement", value: action.financedBy?.trim() }
  ].filter((field) => field.value)

  if (isRiskOpen && risk) return <PhysicalRiskEditModal risk={risk} onClose={onClose} />

  return (
    <Modal title="Mesure de prévention" isLarge isCloseButtonVisible onClose={onClose}>
      <div className="flex flex-col gap-[20px] py-[20px]">
        <div className={`mb-[20px] grid grid-cols-1 items-start gap-[30px] ${risk ? "sm:grid-cols-2" : ""}`}>
          <div className="flex min-w-0 flex-col items-start gap-[14px]">
            <Chip color={isToPlan ? "#8A6D00" : getColor(isOverdue ? "orange" : "blue")} isOutlineColored>
              {statusLabel}
            </Chip>
            {action.name && <div className="whitespace-pre-line text-lg font-normal">{action.name}</div>}
          </div>
          {risk && (
            <div className="min-w-0">
              <button
                type="button"
                className="_hover flex w-full items-center gap-[14px] rounded-[12px] bg-white p-[16px] text-left"
                style={{ border: "1px solid #CBD5E1" }}
                onClick={() => setIsRiskOpen(true)}>
                {riskImageUrl && (
                  <img
                    className="h-[40px] w-[40px] shrink-0 object-contain"
                    src={riskImageUrl}
                    alt={risk.cFamily?.name ?? ""}
                  />
                )}
                <div className="shrink-0">
                  <PhysicalRiskMeter risk={risk} size="sm" />
                </div>
                <span className="min-w-0 break-words text-base font-normal">{risk.cName}</span>
              </button>
            </div>
          )}
        </div>

        {action.description?.trim() && <WizardOverviewField label="Description" value={action.description} />}
        {fields.length > 0 && (
          <div className="grid grid-cols-1 gap-x-[30px] gap-y-[20px] sm:grid-cols-2">
            {fields.map((field) => (
              <WizardOverviewField key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}
